/* eslint-disable max-len */
import type {FormInstance} from 'antd';

import type {
  LciFormValues,
  ProductionDataValues,
  ProductionSiteRow
} from '../../../services/interface/pcfTypes';
import showAlertNotification, {
  dismissAlertNotification
} from '../../../utils/showAlertNotification';
import {
  PCF_SITE_UNITS_SUM_ERROR,
  PCF_SITE_UNITS_SUM_ERROR_KEY,
  PCF_SITE_UNITS_SUM_SUCCESS,
  PCF_SITE_UNITS_SUM_SUCCESS_KEY
} from '../constants/pcfSiteUnitsSumWarning';

/** Reads production the same way persist/save does — includes nested sites when prod tab is unmounted. */
export const getProductionFromForm = (
  form: FormInstance,
  fallback?: ProductionDataValues
): ProductionDataValues | undefined => {
  const fromAll = (form.getFieldsValue(true) as LciFormValues).production;
  const fromField = form.getFieldValue('production') as
    | ProductionDataValues
    | undefined;
  const base = fromAll ?? fromField ?? fallback;

  if (!base) {
    return undefined;
  }

  const registeredSites =
    (form.getFieldValue(['production', 'sites']) as
      | ProductionSiteRow[]
      | undefined) ?? [];
  const allSites = fromAll?.sites ?? fromField?.sites ?? [];
  const formSites =
    registeredSites.length >= allSites.length ? registeredSites : allSites;
  const fallbackSites = fallback?.sites ?? [];
  const siteCount = Math.max(formSites.length, fallbackSites.length);

  if (siteCount === 0) {
    return base;
  }

  const sites = Array.from({length: siteCount}, (_, index) => ({
    ...fallbackSites[index],
    ...formSites[index]
  })) as ProductionSiteRow[];

  return {
    ...base,
    sites
  };
};

const parsePositiveNumber = (value: unknown): number | null => {
  if (value == null || value === '') {
    return null;
  }

  const parsed = parseFloat(String(value));

  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export const sumSiteUnitsProduced = (
  sites: ProductionSiteRow[] | undefined
): number => {
  if (!sites?.length) {
    return 0;
  }

  return sites.reduce((sum, site) => {
    const units = parsePositiveNumber(site.unitsProduced);

    return sum + (units ?? 0);
  }, 0);
};

export const siteUnitsSumMatchesTotal = (
  production: ProductionDataValues | undefined
): boolean => {
  if (!production) {
    return false;
  }

  const totalUnits = parsePositiveNumber(production.units);

  if (totalUnits == null) {
    return false;
  }

  const setup = production.setup;

  if (setup === 'single_site') {
    return true;
  }

  const sites = production.sites ?? [];

  if (!sites.length) {
    return false;
  }

  const siteSum = sumSiteUnitsProduced(sites);

  return Math.abs(siteSum - totalUnits) < 0.0001;
};

export const isProductionDataComplete = (
  production: ProductionDataValues | undefined
): boolean => {
  if (!production) {
    return false;
  }

  const totalUnits = parsePositiveNumber(production.units);

  if (
    totalUnits == null ||
    !production.setup ||
    !production.period ||
    !production.year
  ) {
    return false;
  }

  if (production.setup === 'single_site') {
    return true;
  }

  const sites = production.sites ?? [];

  if (!sites.length || !production.allocationBasis) {
    return false;
  }

  const sitesValid = sites.every((site) => {
    const units = parsePositiveNumber(site.unitsProduced);
    const throughput = parsePositiveNumber(site.facilityThroughput);

    return (
      Boolean(site.name?.trim()) &&
      Boolean(site.state) &&
      units != null &&
      throughput != null
    );
  });

  if (!sitesValid) {
    return false;
  }

  return siteUnitsSumMatchesTotal(production);
};

export const getSiteUnitsSumWarning = (
  production: ProductionDataValues | undefined
): string | null => {
  if (!production || production.setup === 'single_site') {
    return null;
  }

  const totalUnits = parsePositiveNumber(production.units);
  const siteSum = sumSiteUnitsProduced(production.sites);

  if (totalUnits == null || !production.sites?.length) {
    return null;
  }

  if (Math.abs(siteSum - totalUnits) < 0.0001) {
    return null;
  }

  return `Units produced at individual sites (${siteSum.toLocaleString()}) don't match the total you entered (${totalUnits.toLocaleString()}). Please review — even a small discrepancy will affect your emission per unit calculation.`;
};

const canCompareSiteUnitsSum = (
  production: ProductionDataValues | undefined
): boolean => {
  if (!production || production.setup === 'single_site') {
    return false;
  }

  const totalUnits = parsePositiveNumber(production.units);

  if (totalUnits == null || !production.sites?.length) {
    return false;
  }

  return production.sites.some(
    (site) => parsePositiveNumber(site.unitsProduced) != null
  );
};

export const notifySiteUnitsSumOnSiteUnitsChange = (
  production: ProductionDataValues | undefined
): void => {
  if (!canCompareSiteUnitsSum(production)) {
    dismissAlertNotification(PCF_SITE_UNITS_SUM_ERROR_KEY);
    dismissAlertNotification(PCF_SITE_UNITS_SUM_SUCCESS_KEY);

    return;
  }

  const description = getSiteUnitsSumWarning(production);

  if (description) {
    dismissAlertNotification(PCF_SITE_UNITS_SUM_SUCCESS_KEY);
    showAlertNotification({
      type: 'error',
      key: PCF_SITE_UNITS_SUM_ERROR_KEY,
      message: PCF_SITE_UNITS_SUM_ERROR.message,
      description
    });

    return;
  }

  dismissAlertNotification(PCF_SITE_UNITS_SUM_ERROR_KEY);
  showAlertNotification({
    type: 'success',
    key: PCF_SITE_UNITS_SUM_SUCCESS_KEY,
    message: PCF_SITE_UNITS_SUM_SUCCESS.message
  });
};

export const notifySiteUnitsSumMismatchIfNeeded = (
  production: ProductionDataValues | undefined
): boolean => {
  const description = getSiteUnitsSumWarning(production);

  if (!description) {
    return false;
  }

  dismissAlertNotification(PCF_SITE_UNITS_SUM_SUCCESS_KEY);
  showAlertNotification({
    type: 'error',
    key: PCF_SITE_UNITS_SUM_ERROR_KEY,
    message: PCF_SITE_UNITS_SUM_ERROR.message,
    description
  });

  return true;
};
